import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { dataService } from '../../services/dataService'
import { ResourceRoleType } from '../../types'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Plus, EditIcon, Trash2 } from '../icons/Icons'
import { BulkUploadModal } from '../shared/BulkUploadModal'
import s from './shared.module.css'

export default function ResourceRoles() {
  const { organization, orgMember, user } = useAuth()
  const { resourceRoleTypes, refreshData } = useAppData()
  const orgId = organization!.id

  const canBulkUpload = orgMember?.user_type === 'owner' || orgMember?.user_type === 'admin' || orgMember?.user_type === 'manager'

  const [showModal, setShowModal] = useState(false)
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const [editing, setEditing] = useState<ResourceRoleType | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [form, setForm] = useState({ title: '', description: '', cost_rate: 0, billing_rate: 0, currency: 'USD' as any, skills_required: '', active: true })

  const openAdd = () => { setEditing(null); setForm({ title: '', description: '', cost_rate: 0, billing_rate: 0, currency: 'USD', skills_required: '', active: true }); setSaveError(''); setShowModal(true) }
  const openEdit = (r: ResourceRoleType) => { setEditing(r); setForm({ title: r.title, description: r.description, cost_rate: r.cost_rate, billing_rate: r.billing_rate, currency: r.currency, skills_required: r.skills_required, active: r.active }); setSaveError(''); setShowModal(true) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); if (saving) return; setSaving(true); setSaveError('')
    try {
      if (editing) await dataService.updateResourceRoleType(editing.id, form)
      else await dataService.createResourceRoleType(orgId, form as any)
      setShowModal(false); await refreshData()
    } catch (err: any) { setSaveError(err?.message || 'Failed to save. Please try again.') } finally { setSaving(false) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this role type?')) return
    await dataService.deleteResourceRoleType(id); await refreshData()
  }

  return (
    <div>
      <div className={s.pageHeader}>
        <div><h2 className={s.pageTitle}>Role Types</h2><p className={s.pageSubtitle}>{resourceRoleTypes.length} role{resourceRoleTypes.length !== 1 ? 's' : ''} defined</p></div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canBulkUpload && <button className={s.secondaryBtn} onClick={() => setShowBulkUpload(true)}>Bulk Upload</button>}
          <button className={s.primaryBtn} onClick={openAdd}><Plus size={14} /> Add Role Type</button>
        </div>
      </div>

      <div className={s.card}>
        <table className={s.table}>
          <thead><tr><th>Role</th><th>Cost Rate</th><th>Billing Rate</th><th>Currency</th><th>Skills Required</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {resourceRoleTypes.length === 0 && <tr><td colSpan={7} className={s.emptyState}>No role types defined</td></tr>}
            {resourceRoleTypes.map(r => (
              <tr key={r.id}>
                <td style={{ fontWeight: 600 }}>{r.title}</td>
                <td>{r.cost_rate}</td>
                <td>{r.billing_rate}</td>
                <td>{r.currency}</td>
                <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.skills_required || '—'}</td>
                <td><Badge variant={r.active ? 'success' : 'neutral'}>{r.active ? 'Active' : 'Inactive'}</Badge></td>
                <td><div className={s.actions}>
                  <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEdit(r)}><EditIcon size={14} /></button>
                  <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDelete(r.id)}><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showBulkUpload && (
        <BulkUploadModal
          entityType="roleTypes"
          orgId={orgId}
          userId={user!.id}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => { setShowBulkUpload(false); refreshData() }}
        />
      )}

      <Modal open={showModal} title={editing ? 'Edit Role Type' : 'Add Role Type'} onClose={() => setShowModal(false)}
        footer={<><button className={s.secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button><button className={s.primaryBtn} form="roleForm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></>}>
        <form id="roleForm" onSubmit={handleSubmit}>
          {saveError && <div className={s.formError}>{saveError}</div>}
          <div className={s.formGroup}><label>Role Name *</label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required /></div>
          <div className={s.formGroup}><label>Description</label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Cost Rate *</label><input type="number" step="0.01" min="0" value={form.cost_rate} onChange={e => setForm(f => ({ ...f, cost_rate: parseFloat(e.target.value) || 0 }))} required /></div>
            <div className={s.formGroup}><label>Billing Rate *</label><input type="number" step="0.01" min="0" value={form.billing_rate} onChange={e => setForm(f => ({ ...f, billing_rate: parseFloat(e.target.value) || 0 }))} required /></div>
          </div>
          <div className={s.formGroup}><label>Currency *</label>
            <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value as any }))}>
              <option value="USD">USD</option><option value="GBP">GBP</option><option value="INR">INR</option>
            </select>
          </div>
          <div className={s.formGroup}><label>Skills Required</label><textarea value={form.skills_required} onChange={e => setForm(f => ({ ...f, skills_required: e.target.value }))} /></div>
          <div className={s.checkboxGroup}><input type="checkbox" id="rtActive" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /><label htmlFor="rtActive">Active</label></div>
        </form>
      </Modal>
    </div>
  )
}
